/**
 * @fileoverview SkeletonCard — composite card placeholder.
 * @package @puffin-core/skeleton-kit
 *
 * Default rendering: title + 2 text lines. Optional image (top) and action
 * (bottom) via `showImage` / `showAction`. Set `showTitle={false}` to
 * render only text lines (palm-legacy "3 text lines" pattern).
 */

import { memo } from 'react';
import PropTypes from 'prop-types';
import { SkeletonImage } from './SkeletonImage.jsx';
import { SkeletonTitle } from './SkeletonTitle.jsx';
import { SkeletonText } from './SkeletonText.jsx';
import { SkeletonButton } from './SkeletonButton.jsx';

/**
 * @typedef {object} SkeletonCardOwnProps
 * @property {boolean} [showImage=false]
 * @property {boolean} [showTitle=true]
 * @property {number} [textLines=2]
 * @property {boolean} [showAction=false]
 * @property {boolean} [animated=true]
 * @property {string} [className]
 */

/**
 * The remaining props (an `aria-*` override, a test id) are spread onto the element.
 *
 * @typedef {SkeletonCardOwnProps & React.HTMLAttributes<HTMLDivElement>} SkeletonCardProps
 */

/**
 * Memoised; it keeps its `propTypes` declaration until JSDOC-PROP-CONTRACT-001 Phase 3 retires it.
 *
 * @type {React.NamedExoticComponent<SkeletonCardProps> & {propTypes?: object}}
 */
export const SkeletonCard = memo(
  /**
   * @param {SkeletonCardProps} props
   * @returns {React.JSX.Element}
   */
  function SkeletonCard({
    showImage = false,
    showTitle = true,
    textLines = 2,
    showAction = false,
    animated = true,
    className = '',
    'aria-label': ariaLabel = 'Loading content',
    'aria-hidden': ariaHiddenOverride,
    ...props
  }) {
    const classes = ['skeleton-card', className].filter(Boolean).join(' ').trim();

    /** @type {React.HTMLAttributes<HTMLDivElement>} */
    const ariaProps = ariaHiddenOverride
      ? { 'aria-hidden': 'true' }
      : { role: 'status', 'aria-label': ariaLabel };

    return (
      <div className={classes} data-testid="skeleton-card" {...ariaProps} {...props}>
        {showImage && <SkeletonImage animated={animated} />}
        <div className="skeleton-card-content">
          {showTitle && <SkeletonTitle animated={animated} />}
          <SkeletonText lines={textLines} animated={animated} />
          {showAction && <SkeletonButton animated={animated} />}
        </div>
      </div>
    );
  },
);

SkeletonCard.propTypes = {
  showImage: PropTypes.bool,
  showTitle: PropTypes.bool,
  textLines: PropTypes.number,
  showAction: PropTypes.bool,
  animated: PropTypes.bool,
  className: PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-hidden': PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default SkeletonCard;
